"use client";

import Image from "next/image";
import React, { RefObject } from "react";
import { Great_Vibes } from "next/font/google";

const greatVibes = Great_Vibes({
  subsets: ["latin"],
  weight: "400",
});

function drawRose(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  color: string,
  inner: string
) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = inner;
  ctx.lineWidth = Math.max(2, r * 0.12);

  ctx.beginPath();
  ctx.arc(x, y, r * 0.45, 0, Math.PI * 2);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(x, y, r * 0.22, 0, Math.PI * 2);
  ctx.stroke();
}

function drawLeaf(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  rot: number
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.fillStyle = "rgba(148,163,184,0.72)";
  ctx.beginPath();
  ctx.ellipse(0, 0, w, h, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawCornerCluster(
  ctx: CanvasRenderingContext2D,
  width: number,
  x: number,
  y: number,
  flipX = 1,
  flipY = 1
) {
  const scale = width / 1080;

  drawRose(ctx, x + 10 * flipX * scale, y + 12 * flipY * scale, 22 * scale, "#1f4e79", "#173d61");
  drawRose(ctx, x + 45 * flipX * scale, y + 4 * flipY * scale, 18 * scale, "#2f67aa", "#28588e");
  drawRose(ctx, x + 60 * flipX * scale, y + 35 * flipY * scale, 16 * scale, "#173d61", "#102c47");
  drawRose(ctx, x + 5 * flipX * scale, y + 48 * flipY * scale, 15 * scale, "#3f78ba", "#2f67aa");
  drawRose(ctx, x + 28 * flipX * scale, y + 58 * flipY * scale, 13 * scale, "#2f67aa", "#28588e");

  drawLeaf(ctx, x + 78 * flipX * scale, y + 5 * flipY * scale, 12 * scale, 6 * scale, 0.35 * flipX);
  drawLeaf(ctx, x + 80 * flipX * scale, y + 40 * flipY * scale, 12 * scale, 6 * scale, -0.35 * flipX);
  drawLeaf(ctx, x + 18 * flipX * scale, y + 72 * flipY * scale, 12 * scale, 6 * scale, 0.7 * flipX);
  drawLeaf(ctx, x + 68 * flipX * scale, y + 62 * flipY * scale, 12 * scale, 6 * scale, -0.6 * flipX);
}

function drawSideDecor(ctx: CanvasRenderingContext2D, width: number, height: number, x: number) {
  drawLeaf(ctx, x, height * 0.25, width * 0.015, width * 0.007, 0.9);
  drawLeaf(ctx, x, height * 0.35, width * 0.015, width * 0.007, -0.9);
  drawLeaf(ctx, x, height * 0.48, width * 0.015, width * 0.007, 0.9);
  drawLeaf(ctx, x, height * 0.62, width * 0.015, width * 0.007, -0.9);
  drawLeaf(ctx, x, height * 0.75, width * 0.015, width * 0.007, 0.9);

  drawRose(ctx, x, height * 0.32, width * 0.014, "#2f67aa", "#28588e");
  drawRose(ctx, x, height * 0.70, width * 0.014, "#1f4e79", "#173d61");
}

export function drawWeddingFrameToCanvas(
  ctx: CanvasRenderingContext2D,
  source: HTMLVideoElement,
  width: number,
  height: number
) {
  ctx.fillStyle = "#f7f5ef";
  ctx.fillRect(0, 0, width, height);

  const topBannerH = Math.round(height * 0.14);
  const bottomRibbonH = Math.round(height * 0.065);
  const videoTop = topBannerH + Math.round(height * 0.045);
  const videoBottom = height - bottomRibbonH - Math.round(height * 0.11);
  const videoHeight = videoBottom - videoTop;
  const videoLeft = Math.round(width * 0.09);
  const videoWidth = width - videoLeft * 2;

  ctx.fillStyle = "rgba(255,255,255,0.95)";
  ctx.fillRect(
    Math.round(width * 0.12),
    Math.round(height * 0.05),
    Math.round(width * 0.76),
    Math.round(height * 0.11)
  );

  ctx.textAlign = "center";
  ctx.fillStyle = "#173d61";
  ctx.font = `italic ${Math.round(width * 0.06)}px serif`;
  ctx.fillText("John & Maxine", width / 2, Math.round(height * 0.11));

  ctx.drawImage(source, videoLeft, videoTop, videoWidth, videoHeight);
  ctx.strokeStyle = "#2f67aa";
  ctx.lineWidth = Math.max(5, Math.round(width * 0.006));
  ctx.strokeRect(videoLeft - 6, videoTop - 6, videoWidth + 12, videoHeight + 12);

  ctx.fillStyle = "rgba(255,255,255,0.95)";
  ctx.fillRect(
    Math.round(width * 0.18),
    Math.round(height * 0.865),
    Math.round(width * 0.64),
    Math.round(height * 0.055)
  );

  ctx.fillStyle = "#1f4e79";
  ctx.font = `${Math.round(width * 0.028)}px serif`;
  ctx.fillText("14 · 8 · 26", width / 2, Math.round(height * 0.902));

  drawCornerCluster(ctx, width, width * 0.038, height * 0.060, 1, 1);
  drawCornerCluster(ctx, width, width * 0.86, height * 0.060, -1, 1);
  drawCornerCluster(ctx, width, width * 0.038, height * 0.83, 1, -1);
  drawCornerCluster(ctx, width, width * 0.86, height * 0.83, -1, -1);
  drawSideDecor(ctx, width, height, width * 0.052);
  drawSideDecor(ctx, width, height, width * 0.948);
}

function FloralCluster({ className = "" }: { className?: string }) {
  return (
    <div className={`pointer-events-none absolute ${className}`}>
      <div className="relative h-24 w-24">
        <div className="absolute left-1 top-1 h-10 w-10 rounded-full bg-[#1f4e79] shadow-[inset_0_0_0_4px_rgba(255,255,255,0.10)]">
          <div className="absolute inset-[24%] rounded-full border-2 border-[#173d61]/80" />
          <div className="absolute inset-[38%] rounded-full border-2 border-[#173d61]/70" />
        </div>
        <div className="absolute left-8 top-0 h-9 w-9 rounded-full bg-[#2f67aa] shadow-[inset_0_0_0_4px_rgba(255,255,255,0.10)]">
          <div className="absolute inset-[24%] rounded-full border-2 border-[#28588e]/80" />
          <div className="absolute inset-[38%] rounded-full border-2 border-[#28588e]/70" />
        </div>
        <div className="absolute left-11 top-8 h-8 w-8 rounded-full bg-[#173d61] shadow-[inset_0_0_0_4px_rgba(255,255,255,0.10)]">
          <div className="absolute inset-[24%] rounded-full border-2 border-[#102c47]/80" />
          <div className="absolute inset-[38%] rounded-full border-2 border-[#102c47]/70" />
        </div>
        <div className="absolute left-0 top-10 h-8 w-8 rounded-full bg-[#3f78ba] shadow-[inset_0_0_0_4px_rgba(255,255,255,0.10)]">
          <div className="absolute inset-[24%] rounded-full border-2 border-[#2f67aa]/80" />
          <div className="absolute inset-[38%] rounded-full border-2 border-[#2f67aa]/70" />
        </div>
        <div className="absolute left-8 top-14 h-8 w-8 rounded-full bg-[#2f67aa] shadow-[inset_0_0_0_4px_rgba(255,255,255,0.10)]">
          <div className="absolute inset-[24%] rounded-full border-2 border-[#28588e]/80" />
          <div className="absolute inset-[38%] rounded-full border-2 border-[#28588e]/70" />
        </div>
        <div className="absolute left-14 top-2 h-4 w-7 rotate-12 rounded-full bg-[#94a3b8]/70" />
        <div className="absolute left-16 top-14 h-4 w-7 -rotate-12 rounded-full bg-[#94a3b8]/70" />
        <div className="absolute left-3 top-18 h-4 w-7 rotate-45 rounded-full bg-[#94a3b8]/70" />
        <div className="absolute left-16 top-20 h-4 w-7 -rotate-45 rounded-full bg-[#94a3b8]/70" />
      </div>
    </div>
  );
}

function SideFlorals({ side }: { side: "left" | "right" }) {
  const base = side === "left" ? "left-2" : "right-2";

  return (
    <div className={`pointer-events-none absolute ${base} top-20 bottom-20 w-10`}>
      <div className="relative h-full w-full">
        <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-[#94a3b8]/35" />
        <div className="absolute left-1/2 top-[8%] h-4 w-8 -translate-x-1/2 rotate-45 rounded-full bg-[#94a3b8]/55" />
        <div className="absolute left-1/2 top-[18%] h-4 w-8 -translate-x-1/2 -rotate-45 rounded-full bg-[#94a3b8]/55" />
        <div className="absolute left-1/2 top-[30%] h-4 w-8 -translate-x-1/2 rotate-45 rounded-full bg-[#94a3b8]/55" />
        <div className="absolute left-1/2 top-[42%] h-4 w-8 -translate-x-1/2 -rotate-45 rounded-full bg-[#94a3b8]/55" />
        <div className="absolute left-1/2 top-[56%] h-4 w-8 -translate-x-1/2 rotate-45 rounded-full bg-[#94a3b8]/55" />
        <div className="absolute left-1/2 top-[70%] h-4 w-8 -translate-x-1/2 -rotate-45 rounded-full bg-[#94a3b8]/55" />
        <div className="absolute left-1/2 top-[26%] h-7 w-7 -translate-x-1/2 rounded-full bg-[#2f67aa] shadow-[inset_0_0_0_4px_rgba(255,255,255,0.10)]">
          <div className="absolute inset-[26%] rounded-full border-2 border-[#28588e]/80" />
        </div>
        <div className="absolute left-1/2 top-[74%] h-7 w-7 -translate-x-1/2 rounded-full bg-[#1f4e79] shadow-[inset_0_0_0_4px_rgba(255,255,255,0.10)]">
          <div className="absolute inset-[26%] rounded-full border-2 border-[#173d61]/80" />
        </div>
      </div>
    </div>
  );
}

export function WeddingPhotoFrame({
  videoRef,
  captured,
  imageAlt,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  captured: string | null;
  imageAlt: string;
}) {
  return (
    <div className="relative h-full w-full bg-[#f7f5ef]">
      <FloralCluster className="left-2 top-2" />
      <FloralCluster className="right-2 top-2 scale-x-[-1]" />
      <FloralCluster className="bottom-2 left-2 scale-y-[-1]" />
      <FloralCluster className="bottom-2 right-2 scale-x-[-1] scale-y-[-1]" />
      <SideFlorals side="left" />
      <SideFlorals side="right" />

      <div className="absolute left-[11%] top-[5%] w-[78%] bg-[rgba(255,255,255,0.95)] px-4 py-3 text-center">
        <p className={`${greatVibes.className} wedding-gold-halo text-[2.2rem] leading-none text-[#1f4e79] sm:text-5xl`}>
          John &amp; Maxine
        </p>
      </div>

      <div className="absolute left-[8%] top-[18%] h-[63%] w-[84%] border-[4px] border-[#2f67aa] bg-white">
        {!captured ? (
          <video
            ref={videoRef}
            className="relative z-10 h-full w-full object-cover opacity-100"
            style={{ visibility: "visible" }}
            autoPlay
            muted
            playsInline
          />
        ) : (
          <Image
            src={captured}
            alt={imageAlt}
            fill
            unoptimized
            className="object-contain"
          />
        )}
      </div>

      <div className="absolute bottom-[8%] left-[18%] w-[64%] bg-[rgba(255,255,255,0.95)] px-4 py-2 text-center">
        <p className="text-sm tracking-[0.3em] text-[#1f4e79] sm:text-base">
          14 · 8 · 26
        </p>
      </div>
    </div>
  );
}
